import { format } from "date-fns";
import { useEffect, useState } from "react";
import {
	CartesianGrid,
	ComposedChart,
	Line,
	ReferenceArea,
	ReferenceLine,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Card, CardContent } from "@/components/ui/card";
import { getCurrencySymbol } from "@/utils/currency";
import { useApp } from "../../contexts/AppContext";
import { useBaseForecast } from "../../hooks/useBaseForecast";
import { useForecastRunTransactions } from "../../hooks/useForecastRunTransactions";
import { useUserPreferences } from "../../contexts/UserPreferencesContext";
import { useUserPreferenceDefaults } from "../../contexts/UserPreferenceDefaultsContext";
import type { DateBreakdownTransaction } from "../../hooks/useDrillDown";
import {
	generateDateBreakdownDrillDown,
	useDrillDown,
} from "../../hooks/useDrillDown";
import { useFormatting } from "../../hooks/useFormatting";
import {
	getCashMovementTerms,
	getForecastTerm,
} from "../../lib/terminology-config";
import { DrillDownModal } from "../modals/DrillDownModal";

interface ForecastDataPoint {
	date: Date;
	balance: number;
	incoming: number;
	outgoing: number;
}

const CHART_BLACK = "var(--accent-sky-500)";
const CHART_RED = "var(--mc-error-icon)";

interface CustomTooltipProps {
	active?: boolean;
	payload?: any[];
	label?: string;
}

const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
	const { formatCurrency } = useFormatting();
	if (active && payload && payload.length) {
		const data = payload[0].payload;
		const balance = data.balance;
		const movementTerms = getCashMovementTerms();

		return (
			<div className="bg-background border rounded-lg shadow-lg p-3">
				<p
					className="font-semibold text-sm mb-2"
					style={{ color: "var(--accent-sky-500)" }}
				>
					{format(new Date(data.fullDate), "MMM dd, yyyy")}
				</p>
				<div className="space-y-1 text-sm">
					<p className="font-bold" style={{ color: "var(--accent-sky-500)" }}>
						Balance: {formatCurrency(balance, "full")}
					</p>
					<p className="text-neutral-700">
						{movementTerms.in}: {formatCurrency(data.incoming, "full")}
					</p>
					<p className="text-neutral-700">
						{movementTerms.out}: {formatCurrency(data.outgoing, "full")}
					</p>
				</div>
			</div>
		);
	}
	return null;
};

export const ForecastTimelineChart = () => {
	const { formatCurrency } = useFormatting();
	const { mode, currentUser } = useApp();
	const isLive = mode === "live";
	const { baseForecast: forecastData } = useBaseForecast();
	const { getUserPreferences } = useUserPreferences();
	const { defaults } = useUserPreferenceDefaults();
	const userPrefs = getUserPreferences(currentUser.id);

	// Resolve health zone settings: user override → admin default
	const healthyThreshold = userPrefs.healthyThreshold ?? defaults.healthyThreshold;
	const cautionThreshold = userPrefs.cautionThreshold ?? defaults.cautionThreshold;
	const displayMode = userPrefs.chartHealthDashboard90 ?? defaults.chartHealthDashboard90;
	const showBands = displayMode === 'bands';
	const showLine = displayMode === 'bands' || displayMode === 'line';
	const [selectedPoint, setSelectedPoint] = useState<ForecastDataPoint | null>(
		null,
	);
	const [terminologyVersion, setTerminologyVersion] = useState(0);
	const { drillDownData, isOpen, showDrillDown, closeDrillDown } =
		useDrillDown();

	// Forecast run transactions for drill-down (live mode only)
	const { forecastRunId } = useBaseForecast();
	const {
		data: forecastRunTransactions,
		error: runTxnError,
	} = useForecastRunTransactions(forecastRunId, undefined, undefined, isLive);

	// Listen for terminology updates from System Administrator
	useEffect(() => {
		const handleTerminologyUpdate = () => {
			setTerminologyVersion((prev) => prev + 1);
		};
		window.addEventListener("terminology-updated", handleTerminologyUpdate);
		return () =>
			window.removeEventListener(
				"terminology-updated",
				handleTerminologyUpdate,
			);
	}, []);

	// suppress unused warning
	void terminologyVersion;

	const movementTerms = getCashMovementTerms();
	const forecastTerm = getForecastTerm();

	const chartData = forecastData.map((point) => ({
		date: format(point.date, "MMM dd"),
		fullDate: point.date.toISOString(),
		balance: Math.round(point.balance),
		incoming: Math.round(point.incoming),
		outgoing: Math.round(point.outgoing),
	}));

	const triggerDrillDown = (point: any) => {
		if (!point?.fullDate) return;
		const pointDate = new Date(point.fullDate);
		setSelectedPoint({
			date: pointDate,
			balance: point.balance,
			incoming: point.incoming,
			outgoing: point.outgoing,
		});
		const dateStr = format(pointDate, "MMMM dd, yyyy");

		// In live mode, filter forecast run transactions for this specific day
		let actualTransactions: DateBreakdownTransaction[] | undefined;
		if (isLive && runTxnError) {
			actualTransactions = [];
		} else if (isLive && forecastRunTransactions) {
			const dayStr = format(pointDate, "yyyy-MM-dd");
			actualTransactions = forecastRunTransactions
				.filter((txn) => format(new Date(txn.forecastDate), "yyyy-MM-dd") === dayStr)
				.map((txn) => ({
					name: txn.counterpartyName ?? "Unknown",
					description: txn.description,
					grossValueIn: txn.direction === "inflow" ? txn.grossAmount : 0,
					grossValueOut: txn.direction === "outflow" ? txn.grossAmount : 0,
					category: txn.category,
					dataSourceName: txn.dataSourceName ?? undefined,
				}));
		} else if (isLive) {
			actualTransactions = [];
		}

		const drillDown = generateDateBreakdownDrillDown(
			dateStr,
			point.balance,
			point.incoming,
			point.outgoing,
			pointDate,
			undefined,
			actualTransactions,
		);
		if (isLive && runTxnError) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Transaction Load Error", value: runTxnError instanceof Error ? runTxnError.message : "Failed to load transactions" },
			];
		} else if (isLive && !forecastRunId) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: "Run a forecast refresh to enable transaction drill-down" },
			];
		} else if (isLive && !forecastRunTransactions) {
			drillDown.context = [
				...(drillDown.context ?? []),
				{ label: "Note", value: "Loading transactions…" },
			];
		}
		showDrillDown("forecast-point", drillDown);
	};

	const handlePointClick = (data: any) => {
		if (data?.activePayload?.[0]) {
			triggerDrillDown(data.activePayload[0].payload);
		}
	};

	const ClickableDot = (props: any) => {
		const { cx, cy, payload } = props;
		if (!cx || !cy) return null;
		return (
			<circle
				cx={cx}
				cy={cy}
				r={5}
				fill={CHART_BLACK}
				stroke="white"
				strokeWidth={2}
				style={{ cursor: "default" }}
				onClick={() => triggerDrillDown(payload)}
			/>
		);
	};

	return (
		<>
			<DrillDownModal
				isOpen={isOpen}
				onClose={closeDrillDown}
				data={drillDownData}
			/>

			<Card className="col-span-full" data-tour="timeline-chart">
				<div className="flex items-center justify-between border-b px-4 py-3">
					<div className="flex items-center gap-1">
						<span className="text-lg font-semibold text-neutral-900">
							90-Day Cash Flow {forecastTerm}
						</span>
						<InfoTooltip
							title={`90-Day Cash Flow ${forecastTerm}`}
							content={`Interactive chart showing your projected cash position over the next 90 days. Hover any point on the chart for a detailed breakdown.`}
						/>
					</div>
				</div>
				<CardContent className="pt-4">
					<div className="space-y-4">
						{/* Legend — only shown when zones or line are active */}
						{displayMode !== 'off' && (
							<div className="flex items-center gap-6 flex-wrap">
								{showBands && (
									<>
										<div className="flex items-center gap-2">
											<div
												className="w-3 h-3 rounded"
												style={{
													backgroundColor: "var(--mc-success-bg)",
													border: "1px solid var(--mc-success-icon)",
												}}
											/>
											<span className="text-xs text-neutral-600">
												Healthy (&gt;{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)
											</span>
										</div>
										<div className="flex items-center gap-2">
											<div
												className="w-3 h-3 rounded"
												style={{
													backgroundColor: "var(--mc-warning-bg)",
													border: "1px solid var(--mc-warning-icon)",
												}}
											/>
											<span className="text-xs text-neutral-600">
												Caution ({getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K–{getCurrencySymbol()}{(healthyThreshold / 1000).toFixed(0)}K)
											</span>
										</div>
										<div className="flex items-center gap-2">
											<div
												className="w-3 h-3 rounded"
												style={{
													backgroundColor: "var(--mc-error-bg)",
													border: "1px solid var(--mc-error-icon)",
												}}
											/>
											<span className="text-xs text-neutral-600">
												Critical (&lt;{getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K)
											</span>
										</div>
									</>
								)}
								{showLine && (
									<div className="flex items-center gap-2">
										<div className="w-8 h-0 border-t-2 border-dashed border-red-500" />
										<span className="text-xs text-neutral-600">
											Threshold ({getCurrencySymbol()}{(cautionThreshold / 1000).toFixed(0)}K)
										</span>
									</div>
								)}
							</div>
						)}

						{/* Chart */}
						<div className="h-[400px] w-full" style={{ cursor: "default" }}>
							<ResponsiveContainer width="100%" height="100%">
								<ComposedChart
									data={chartData}
									onClick={handlePointClick}
									margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
								>
									<CartesianGrid
										strokeDasharray="3 3"
										className="stroke-muted"
									/>
									<XAxis
										dataKey="date"
										tick={{ fontSize: 12 }}
										interval={Math.floor(chartData.length / 10)}
									/>
									<YAxis
										tick={{ fontSize: 12 }}
										tickFormatter={(value) =>
											`${getCurrencySymbol()}${(value / 1000).toFixed(0)}K`
										}
									/>
									<Tooltip content={<CustomTooltip />} />

									{/* Health zone backgrounds — only when bands mode */}
									{showBands && (
										<>
											<ReferenceArea
												y1={healthyThreshold}
												y2={10000000}
												fill="var(--mc-success-bg)"
												fillOpacity={0.3}
												ifOverflow="hidden"
											/>
											<ReferenceArea
												y1={cautionThreshold}
												y2={healthyThreshold}
												fill="var(--mc-warning-bg)"
												fillOpacity={0.3}
												ifOverflow="hidden"
											/>
											<ReferenceArea
												y1={-10000000}
												y2={cautionThreshold}
												fill="var(--mc-error-bg)"
												fillOpacity={0.3}
												ifOverflow="hidden"
											/>
										</>
									)}

									{/* Threshold line — shown for bands and line modes */}
									{showLine && <ReferenceLine
										y={cautionThreshold}
										stroke={CHART_RED}
										strokeDasharray="5 5"
										strokeWidth={2}
										label={{
											value: "Threshold",
											position: "insideTopLeft",
											fill: CHART_RED,
											fontSize: 12,
										}}
									/>}

									{/* Main balance line — provides reliable click targets */}
									<Line
										type="monotone"
										dataKey="balance"
										stroke={CHART_BLACK}
										strokeWidth={3}
										name={`Base ${getForecastTerm(true)}`}
										dot={false}
										activeDot={{ r: 5, fill: CHART_BLACK, cursor: "default" }}
									/>
								</ComposedChart>
							</ResponsiveContainer>
						</div>

						{/* Selected Point Details */}
						{selectedPoint && (
							<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
								<h4 className="font-semibold text-sm mb-2">
									Selected Date: {format(selectedPoint.date, "MMMM dd, yyyy")}
								</h4>
								<div className="grid grid-cols-3 gap-4 text-sm">
									<div>
										<p className="text-muted-foreground">Projected Balance</p>
										<p className="font-bold text-lg">
											{formatCurrency(selectedPoint.balance, "full")}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground">
											Expected {movementTerms.in}
										</p>
										<p className="font-bold text-lg text-green-600">
											{formatCurrency(selectedPoint.incoming, "full")}
										</p>
									</div>
									<div>
										<p className="text-muted-foreground">
											Expected {movementTerms.out}
										</p>
										<p className="font-bold text-lg text-red-600">
											{formatCurrency(selectedPoint.outgoing, "full")}
										</p>
									</div>
								</div>
							</div>
						)}
					</div>
				</CardContent>
			</Card>
		</>
	);
};
